import React, { useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faUserEdit,
    faUserMinus,
    faBars,
    faFileAlt,
    faUserGroup
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import DeleteModal from '@shared/components/DeleteModal';
import Nomenclatures from './Nomenclatures';
import UserGroupsMenu from './UserGroupsMenu';
import { userGroupsService } from '@modules/userGroups/services/userGroupsService';
import useModal from '@shared/hooks/useModal';
import UsersGroupForm from './UserGroupsForm'
import useFetchData from '@shared/hooks/useFetchData';
import { useGroupTable } from '@modules/userGroups/hooks/useGroupTable';
import UsersByGroup from '@modules/userGroups/componenets/UsersByGroup';
import Table from '@shared/components/Table';

export default function UserGroup() {
    const { t } = useTranslation();
    const { modalState, handleOpenModal, handleCloseModal, setModalState } = useModal();

    const {
        data,
        pageNumber,
        sortBy,
        sortDir,
        pageSize,
        selectedData,
        totalElements,
        totalPages,
        searchQuery,
        setSearchQuery,
        handleSearchClick,
        setSelectedData,
        setPageSize,
        handleSort,
        fetchData,
        handleError,
        handleCheckboxChange,
        setpageNumber,
    } = useFetchData();

    const {
        groupData,
        getData,
        searchOptions,
        selectedGroupName,
        handleEditGroup,
        handleAddCloseModal,
        handleConfirmDelete,
        handleSaveMenus,
        handleOpenMenu,
        handleCloseUsersModal
    } = useGroupTable({
        fetchData,
        selectedData,
        data,
        handleOpenModal,
        handleCloseModal,
        handleError,
        setSelectedData
    });

    useEffect(() => {
        fetchData(userGroupsService.getGroups, {
            sortBy: sortBy || 'name',
            sortDir: sortDir
        });
    }, [pageNumber, pageSize, sortBy, sortDir]);

    const columns = [
        { key: 'name', label: t('users.table.search.options.groupName'), sortable: true },
        { key: 'ldapGroupId', label: t('users.table.search.options.LDAP'), sortable: true },
        { key: 'isAdministrative', label: t('users.table.search.options.administratorRights'), sortable: false }
    ];

    const actionButtons = (
        <>
            <button
                className="icon-button"
                title={t('common.add')}
                onClick={() => handleOpenModal('add')}
            >
                <FontAwesomeIcon icon={faUserPlus} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={handleEditGroup}
                title={t('common.edit')}
            >
                <FontAwesomeIcon icon={faUserEdit} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => handleOpenModal('delete')}
                title={t('common.remove')}
            >
                <FontAwesomeIcon icon={faUserMinus} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                onClick={() => selectedData && handleOpenModal('users')}
                title={t('common.users')}
                disabled={!selectedData}
            >
                <FontAwesomeIcon icon={faUserGroup} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                title={t('common.menu')}
                onClick={handleOpenMenu}
            >
                <FontAwesomeIcon icon={faBars} />
            </button>
            <button
                className={`icon-button ${!selectedData ? 'disabled-link' : ''}`}
                title={t('nav.userGroups.notifications.nomenclatures')}
                onClick={() => handleOpenModal('nomenclatures')}
            >
                <FontAwesomeIcon icon={faFileAlt} />
            </button>
        </>
    );

    return (
        <>
            <Table
                // Data and state
                data={data}
                loading={false}
                error={null}
                selectedData={selectedData}
                pageNumber={pageNumber}
                pageSize={pageSize}
                sortBy={sortBy}
                sortDir={sortDir}
                totalElements={totalElements}
                totalPages={totalPages}
                searchQuery={searchQuery}
                searchOptions={searchOptions}

                // Column configuration
                columns={columns}

                // Title and actions
                title={t('users.groupTitle', 'Groups')}
                actionButtons={actionButtons}

                // Handlers
                handleSort={handleSort}
                handleCheckboxChange={handleCheckboxChange}
                handleSearchClick={handleSearchClick}
                setSearchQuery={setSearchQuery}
                setPageSize={setPageSize}
                setpageNumber={setpageNumber}
                getData={getData}

                // Custom renderers
                renderCell={(item, column) => {
                    if (column.key === 'isAdministrative') {
                        const isAdmin = item.isAdministrative === true || item.isAdministrative === 'y';
                        return (
                            <span className={`status-badge ${isAdmin ? 'active' : 'inactive'}`}>
                                {isAdmin ? 'true' : 'false'}
                            </span>
                        );
                    }
                    return item[column.key] || '';
                }}
            />

            {modalState.visible && modalState.type === 'add' && (
                <UsersGroupForm
                    onClose={handleAddCloseModal}
                    userGroup={groupData}
                    selectedData={selectedData}
                    fetchData={fetchData}
                />
            )}

            {modalState.visible && modalState.type === 'delete' && (
                <DeleteModal
                    onClose={handleCloseModal}
                    onConfirm={handleConfirmDelete}
                    entityType='record'
                    selectedData={selectedData}
                />
            )}

            {modalState.visible && modalState.type === 'nomenclatures' && (
                <Nomenclatures
                    onClose={handleCloseModal}
                    groupData={data[selectedData]}
                />
            )}

            {modalState.visible && modalState.type === 'menu' && (
                <UserGroupsMenu
                    onClose={handleCloseModal}
                    onSave={handleSaveMenus}
                    groupData={groupData}
                />
            )}

            {modalState.visible && modalState.type === 'users' && (
                <UsersByGroup
                    onClose={handleCloseUsersModal}
                    groupId={selectedData}
                    groupName={selectedGroupName}
                />
            )}
        </>
    );
}